import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../../layouts/AuthLayout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/Form/Input';
import { authService } from '../../services/auth.service';
import { getPasswordValidationError, PASSWORD_REQUIREMENTS_MESSAGE } from '@ministry-of-wine/shared';

const getErrorMessage = (error: unknown) => {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: string | string[] }).message;
    if (Array.isArray(message)) {
      return message[0];
    }
    if (typeof message === 'string') {
      return message;
    }
  }

  return 'Unable to reset password.';
};

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setError('');
    setSuccess('');
    setPasswordError(value ? getPasswordValidationError(value) || '' : '');

    if (confirmPassword) {
      setConfirmPasswordError(confirmPassword !== value ? 'Passwords do not match.' : '');
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    setError('');
    setSuccess('');
    setConfirmPasswordError(value && value !== password ? 'Passwords do not match.' : '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError('This reset link is invalid or missing a token.');
      return;
    }

    const passwordValidationError = getPasswordValidationError(password);

    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      setError(passwordValidationError);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.');
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.resetPassword({ token, password });
      setSuccess(response.message || 'Your password has been reset successfully.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="bg-[#F6F5F3] min-h-screen w-full flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-neutral-900">Reset password</h1>
            <p className="mt-2 text-neutral-600 text-sm">
              Set a new password for your account.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg text-error-700 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="New password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              error={passwordError}
              hint={PASSWORD_REQUIREMENTS_MESSAGE}
              required
            />

            <Input
              label="Confirm password"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              error={confirmPasswordError}
              required
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isLoading}
              className="w-full"
            >
              Reset password
            </Button>
          </form>

          <p className="text-center text-neutral-600 text-sm mt-6">
            <Link to="/login" className="text-[#404040] hover:text-[#333333] font-bold">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
